import * as d3 from "d3";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { drawRect, drawArc, drawCircle, drawLine, createCustomPattern } from "../utils/drawPitchfunctions";
import PlayerCard from "./PlayerCard";
import { IPlayer } from "../types/IPassingNetworkData";
import { ICustomPatternData } from "../types/ICustomPatternData";
import { IPlayerForVoronoi } from "../types/IVoronoiData";
import { IPitchProps, IZoneArray } from "../types/IPitchProps";
import { useTheme } from "../contexts/ThemeContext";
import { createZone } from "../utils/createZone";
import { useSearch } from "../contexts/PlayerSearchContext";
import { drawChemistrys } from "../utils/drawChemistrys";
import { drawHull, drawPlayerPassingNetwork } from "../utils/drawPlayerPassingNetworks";
import TeamSelect from "../assets/icons/TeamSelect.svg";
import { drawGameModelHull, drawGameModelPlayers, drawMiddleRect, drawPlayerImages, drawPlayerNames, getColorByValue } from "../utils/pitchWidgetUtils";
import Playfield from "./../@mc-playfield/base";
import { SingleGameVisualisationMode } from "./../@mc-playfield/base";
import { generateRiskMap } from "../utils/riskMapGenerator";
import { renderPassingAttack } from "../utils/drawPassingAttack";
import { drawModuleChemistrys } from "../utils/drawModuleChemistry";
import { renderRiskMapForAttributes } from "../utils/drawRiskMapForAttributes";
import { ModuleSpinner } from "../ui/LoadingSpinner/ModuleSpinner";
import { IBuildupPlayOnPitchData, IFinalThirdPitchData } from "./PlayerDetail/Overview/AttributesOnPitch";
import { drawGameModelVoronoi, drawVoronoi } from "../utils/drawVoronoi";
import { teamSegmentGameModelGroup } from "./ClubDetail/Performance/GameModel";
import { drawGameModelContours } from "../utils/drawGameModelContours";
import { IGameModelSimulation } from "../types/IClubDetail";

const zoneArray: IZoneArray[] = [
  { id: 1, name: "Attack" },
  { id: 2, name: "Left Wing Attack" },
  { id: 3, name: "Right Wing Attack" },
  { id: 4, name: "Attacking midfield" },
  { id: 5, name: "Central Midfield" },
  { id: 6, name: "Defensive Midfield" },
  { id: 7, name: "Left central Defence" },
  { id: 8, name: "Right central Defence" },
  { id: 9, name: "Left Wing Defence" },
  { id: 10, name: "Right Wing Defence" },
  { id: 11, name: "Goalkeeper" }
];

const Pitch: React.FC<IPitchProps> = (pitchProps) => {

  const { selectedAttr } = useSearch();
  const pitchRef = useRef<HTMLDivElement | null>(null);
  const [pitchWidth, setPitchWidth] = useState<number>(0);
  const [pitchHeight, setPitchHeight] = useState<number>(0);
  useEffect(() => {
    const giveSizeToPitch = () => {
      if (pitchRef.current) {
        const pitchWidth = pitchRef.current.clientWidth;
        const pitchHeight = pitchWidth * 0.625; // Maintain 5:8 aspect ratio
        setPitchDimensions({ width: pitchWidth, height: pitchHeight });
        setPitchWidth(pitchWidth);
        setPitchHeight(pitchHeight);
        // const container = document.getElementById("football-pitch-container")!;
        // setPitchWidth(container.clientWidth);
        // setPitchHeight(container.clientWidth * 0.625);
      }
    };
    giveSizeToPitch();
    window.addEventListener("resize", giveSizeToPitch);
    return () => {
      window.removeEventListener("resize", giveSizeToPitch);
    };
  }, []);
  const { theme } = useTheme();
  const { handleSelectedZoneIdsChange } = useSearch();
  const svgRef = useRef<SVGSVGElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [selectedPlayer, setSelectedPlayer] = useState<IPlayer | IPlayerForVoronoi | null>(null);
  const [showPlayerCard, setShowPlayerCard] = useState<boolean>(false);
  const [cardPosition, setCardPosition] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const [selectedAreas, setSelectedAreas] = React.useState<string[]>([]);
  const [pitchDimensions, setPitchDimensions] = useState({ width: window.innerWidth, height: window.innerHeight });
  

  const getSelectedZoneIds = (selectedZoneNames: string[]): IZoneArray[] => {
    return selectedZoneNames.map((selectedZoneName) => {
      //const selectedZone = zoneArray.find(zone => zone.name.split(' ').join('') === selectedZoneName);
      let selectedZone = zoneArray.find((zone) => zone.name === selectedZoneName);
      if (!selectedZone) {
        selectedZone = zoneArray.find((zone) => zone.name.split(" ").join("") === selectedZoneName);
      }
      if (selectedZone) {
        return { id: selectedZone.id, name: selectedZone.name };
      } else {
        return { id: 0, name: selectedZoneName };
      }
    });
  };

  const getSelectedZones = (selectedZones: number[]) => {
    return selectedZones.map((selectedZone) => {
      const selectedZoneIdArray = zoneArray.find((zone) => zone.id === selectedZone);
      if (selectedZoneIdArray) {
        return { id: selectedZoneIdArray.id, name: selectedZoneIdArray.name };
      } else {
        return { id: 0, name: "" };
      }
    });
  };

  const handleSelectedAreasChange = (newSelectedAreas: string[]) => {
    setSelectedAreas(newSelectedAreas);
    const selectedZones = getSelectedZoneIds(newSelectedAreas);
    pitchProps.onSelectedZonesChange?.(selectedZones);
    //handleSelectedZoneIdsChange(selectedZones);
  };

  useEffect(() => {
    if (pitchProps.selectedZones && pitchProps.selectedZones?.length > 0) {
      const selectedZones = getSelectedZones(pitchProps.selectedZones);
      const selectedZonesNameArray: string[] = [];
      pitchProps.selectedZones.map((selectedZone) => {
        const selectedZoneName = zoneArray.find((zone) => zone.id === selectedZone)?.name;
        selectedZoneName && selectedZonesNameArray.push(selectedZoneName);
      });
      setSelectedAreas(selectedZonesNameArray);
      handleSelectedZoneIdsChange(selectedZones);
    } else {
      setSelectedAreas([]);
      handleSelectedZoneIdsChange([]);
    }
  }, [pitchProps.selectedZones]);

  const createFootballPitch = useCallback(() => {
    if (pitchRef.current) {
      const pitchWidth = pitchProps?.width ? pitchProps?.width : pitchRef.current.clientWidth;
      const pitchHeight = pitchWidth * 0.625; // Maintain 5:8 aspect ratio
      setPitchDimensions({ width: pitchWidth, height: pitchHeight });
    }
    // const container = document.getElementById("football-pitch-container")!;
    // const pitchWidth: number = container.clientWidth;
    // const pitchHeight: number = pitchWidth * 0.625; // Maintain a 5:8 aspect ratio
    // setPitchDimensions({ width: pitchWidth, height: pitchHeight });
    const areaHeight1: number = pitchHeight / 4;
    const areaHeight2: number = areaHeight1 * 2;
    const svg: d3.Selection<SVGSVGElement, unknown, any, undefined> = d3
      .select<SVGSVGElement, unknown>(svgRef.current!)
      .attr("width", pitchWidth)
      .attr("height", pitchHeight);
    const scale: number = pitchWidth / 800;
    svg.selectAll("*").remove();
    const PatternData: ICustomPatternData[] = [
      { x: 3.5, y: 0, width: 2, height: 10 },
      { x: 0, y: 3.5, width: 10, height: 2 },
    ];
    createCustomPattern(svg, 60, 60, PatternData, theme.NumberColor);
    svg.append("filter").attr("id", "blurFilter").append("feGaussianBlur").attr("stdDeviation", 5)
    // .style("stroke", theme.NumberColor)
    // .style("stroke-width", 1);
    // Draw the pitch outline
    svg
      .append("rect")
      .attr("width", pitchWidth)
      .attr("height", pitchHeight)
      .attr("rx", 8)
      .attr("ry", 8)
      .style("fill", theme.chemistryPitchFill)
      // .style("stroke", theme.NumberColor)
      // .style("stroke-width", 1)
      .style("stroke-linecap", "round") // Ensures rounded ends for the stroke
      .style("stroke-linejoin", "round")
      .on("wheel", (event) => {
        event.stopPropagation();
      })
      .on("click", (event) => {
        if (pitchProps?.playerCardID) {
          pitchProps?.setPlayerCardID!(0);
        }
      });
    {
      pitchProps.isPattern &&
        svg.append("rect").attr("width", pitchWidth).attr("height", pitchHeight).style("fill", "url(#custom-pattern)");
    }
    const xScale = d3.scaleLinear().domain([0, 105]).range([0, pitchWidth]);
    const yScale = d3.scaleLinear().domain([0, 68]).range([0, pitchHeight]);
    const riskMapGroup: d3.Selection<SVGGElement, any, null, undefined> = svg.append<SVGGElement>("g");
    riskMapGroup.attr("viewBox", `0 0 ${pitchWidth} ${pitchHeight}`);



    const pitchGroup: d3.Selection<SVGGElement, any, null, undefined> = svg
      .append<SVGGElement>("g")
      .attr("width", pitchWidth)
      .attr("height", pitchHeight)
      .on("wheel", (event) => {
        event.stopPropagation();
      });

    if (pitchProps?.playerCardID && pitchProps?.playerCardID > 0) {
      pitchGroup.attr("style", "filter: blur(5px);");
      pitchGroup.on("click", () => pitchProps?.setPlayerCardID!(0));
    } else {
      pitchGroup.attr("style", "filter: none;");
      pitchGroup.on("click", () => { });
    }
    {
      pitchProps.isZones &&
        createZone(pitchGroup, pitchWidth, pitchHeight, selectedAreas, setSelectedAreas, xScale, yScale, handleSelectedAreasChange);
    }
    if (pitchProps.currentView === "attributesOnPitch" && (selectedAttr === 'Finishing - Shots' || selectedAttr === 'Finishing - Aerial') && pitchProps.finalThirdData) {
      const customDomain = [1, 0.5, 0, -0.3, -0.6]; // High to low values
      const customColors = [
        "#00D1A8", // Highly effective
        "#4AF075", // Effective
        "#F2E94E", // Average
        "#FF7800", // Below average
        "#FF3864"  // Poor (optional)
      ];
      let finalDomain: number[];
      finalDomain = customDomain;
      const tooltip = d3
        .select("body")
        .append("div")
        .attr("class", "custom-tooltip")
        .style("position", "fixed")
        .style("z-index", "999")
        .style("visibility", "hidden")
        .style("background-color", theme.secondaryButtonBackground)
        .style("color", theme.color)
        .style("border", `1px solid ${theme.secondaryButtonBackground}`)  
        .style("padding", "8px")
        .style("border-radius", "4px")
        .style("box-shadow", "0px 0px 6px rgba(0, 0, 0, 0.1)")
        .style("pointer-events", "none")
        .style("font-size", "12px")

      const colorScale = d3.scaleLinear<string>()
        .domain(finalDomain)
        .range(customColors)
        .interpolate(d3.interpolateRgb); // or d3.interpolateHcl for smoother transitions

      svg.selectAll(".circle")
        .data(pitchProps.finalThirdData)
        .enter()
        .append("circle")
        .attr("class", "final-third-circle")
        .attr("cx", d => xScale(d.locationX))
        .attr("cy", d => yScale(d.locationY))
        .attr("r", 8 * scale)
        .attr("fill", d => colorScale(d.score)).on("mouseover", (event, d) => {
          tooltip
            .style("visibility", "visible")
            .html(`
                <strong>Score:</strong> ${d.score}<br/>
                <strong>Match:</strong> ${d.matchLabel}<br/>
                <strong>Match Date:</strong> ${new Date(d.dateTime).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric"
                })}<br/>
              `);
        })
        .on("mousemove", (event) => {
          tooltip
            .style("top", `${event.clientY + 10}px`)
            .style("left", `${event.clientX + 10}px`);
        })
        .on("mouseout", () => {
          tooltip.style("visibility", "hidden");
        });
    }

    if (pitchProps.currentView === "attributesOnPitch" && selectedAttr === 'Crossing' && pitchProps?.buildupPlayData! && pitchProps?.buildupPlayData.length > 0) {

            const tooltip = d3
              .select("body")
              .append("div")
              .attr("class", "custom-tooltip") 
              .style("position", "fixed")
              .style("z-index", "999")
              .style("visibility", "hidden")
              .style("background-color", theme.secondaryButtonBackground)
              .style("color", theme.color)
              .style("border", `1px solid ${theme.secondaryButtonBackground}`)
              .style("padding", "8px")
              .style("border-radius", "4px")
              .style("box-shadow", "0px 0px 6px rgba(0, 0, 0, 0.1)")
              .style("pointer-events", "none")
              .style("font-size", "12px")

            const colorScale = d3.scaleLinear<string>()
              .domain([10, 30, 50, 65, 100])
              .range(["#FF3864", "#FF7800", "#F2E94E", "#4AF075", "#00D1A8"])
              .interpolate(d3.interpolateLab);

            let circleLine : any
            let circle : any
            svg
                    .append("defs")
                    .append("marker")
                    .attr("id", "arrowhead")
                    .attr("viewBox", "0 0 10 10")
                    .attr("refX", 5) // align the center of the `>` tip with the end of the line
                    .attr("refY", 5)
                    .attr("markerWidth", 10)
                    .attr("markerHeight", 10)
                    .attr("orient", "auto")
                    .attr("markerUnits", "userSpaceOnUse") // ensure fixed size
                    .append("path")
                    .attr("d", "M0,0 L5,5 L0,10") // draw the `>` shape
                    .attr("fill", "none")
                    .attr("stroke", "#00A1E4")
                    .attr("stroke-width", 1)
            
           pitchProps.buildupPlayData?.forEach(cellData => {
              if (
                cellData.locationX != null &&
                cellData.locationY != null
              ) {
                const centerX = xScale(cellData.locationX);
                const centerY = yScale(cellData.locationY);

                let arrowLine: any = null;
                let highlightCircle: any = null;

                // Append main data circle
                const dataCircle = pitchGroup.append("circle")
                  .attr("class", "final-third-circle")
                  .attr("cx", centerX)
                  .attr("cy", centerY)
                  .attr("r", 8 * scale)
                  .attr("fill", colorScale(cellData?.score ?? 0))
                  .style("cursor", "pointer");

                // If pass data valid, draw hidden line + circle
                if (
                  cellData.passAngle !== 0 &&
                  cellData.passAngle !== null &&
                  cellData.passLength !== 0 &&
                  cellData.passLength !== null
                ) {
                  const angleInDegrees = cellData.passAngle;
                  const scaleFactor = 0.5;
                  const length = cellData.passLength;

                  const angleInRadians = (angleInDegrees * Math.PI) / 180;
                  const endX = centerX + xScale(length) * Math.cos(angleInRadians);
                  const endY = centerY + yScale(length) * Math.sin(angleInRadians);

                  highlightCircle = svg.append("circle")
                    .attr("cx", centerX)
                    .attr("cy", centerY)
                    .attr("r", 4)
                    .style("fill", "#00A1E4")
                    .style("visibility", "hidden");

                  arrowLine = svg.append("line")
                    .attr("x1", centerX)
                    .attr("y1", centerY)
                    .attr("x2", endX)
                    .attr("y2", endY)
                    .attr("stroke", "#00A1E4")
                    .attr("stroke-width", 2)
                    .attr("marker-end", "url(#arrowhead)")
                    .style("visibility", "hidden");
                }

                // Hover interactions
                dataCircle
                  .on("mouseover", (event) => {
                    tooltip
                      .style("visibility", "visible")
                      .html(`
                        <strong>Score:</strong> ${cellData.score}<br/>
                        <strong>Match:</strong> ${cellData.matchLabel}<br/>
                        <strong>Pass Angle:</strong> ${cellData.passAngle}<br/>
                        <strong>Pass Length:</strong> ${cellData.passLength}<br/>
                        <strong>Match Date:</strong> ${new Date(cellData.dateTime).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric"
                        })}<br/>
                      `);

                    arrowLine?.style("visibility", "visible");
                    highlightCircle?.style("visibility", "visible");
                  })
                  .on("mousemove", (event) => {
                    tooltip
                      .style("top", `${event.clientY + 10}px`)
                      .style("left", `${event.clientX + 10}px`);
                  })
                  .on("mouseout", () => {
                    tooltip.style("visibility", "hidden");
                    arrowLine?.style("visibility", "hidden");
                    highlightCircle?.style("visibility", "hidden");
                  });
              }
            });


    }

    //heat map
    if (pitchProps.currentView === 'attributesOnPitch' && (selectedAttr === 'Creativity' || selectedAttr === 'Passing accuracy' || selectedAttr === 'Dribbling') && pitchProps?.buildupPlayData! && pitchProps?.buildupPlayData.length > 0) {
      
      const maxPassCount = d3.max(pitchProps.buildupPlayData, (d) => d.count) ?? 1;
      const getBackgroundColor = (vaepScore: number, opacity: any) => {
        if (vaepScore == null) {
          return "none";
        }
        if (vaepScore >= 0 && vaepScore <= 19.99) {
          return "var(--UI-red-500, #FF3864)";
        } else if (vaepScore >= 20 && vaepScore <= 39.99) {
          return "var(--Brand-orange-500, #FF7800)";
        } else if (vaepScore >= 40 && vaepScore <= 59.99) {
          return "var(--UI-yellow-500, #F2E94E)";
        } else if (vaepScore >= 60 && vaepScore <= 79.99) {
          return "var(--Brand-green-2-500, #4AF075)";
        } else if (vaepScore >= 80 && vaepScore <= 100) {
          if (vaepScore >= 98 && opacity >= 0.6) {
            return "#00D1A8";
          } else {
            return "#4AF075";
          }
        } else {
          return "red";
        }
      };

      type ColorFunction = (vaepScore: number, opacity: any) => string;
      const createPitchGrid = (
        pitchGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
        xScale: d3.ScaleLinear<number, number>,
        yScale: d3.ScaleLinear<number, number>,
        attributesData: IBuildupPlayOnPitchData[],
        getBackgroundColor: ColorFunction,
        maxPassCount: number
      ): void => {
        const gridColumns = 20;
        const gridRows = 20;
        const padding = 2;
        const cellWidth = 105 / gridColumns;
        const cellHeight = 68 / gridRows;
        const gridData = new Map();
        for (let col = 0; col < gridColumns; col++) {
          for (let row = 0; row < gridRows; row++) {
            const key = `${col},${row}`;
            gridData.set(key, {
              vaep_score: null,
              passCount: 0,
              hasData: false,
              passAngle: 0,
              passLength: 0,
            });
          }
        }
        attributesData.forEach((d) => {
          const col = Math.floor(d.locationX / cellWidth);
          const row = Math.floor(d.locationY / cellHeight);
          const key = `${col},${row}`;
          if (col >= 0 && col < gridColumns && row >= 0 && row < gridRows) {
            gridData.set(key, {
              vaep_score: d.score,
              passCount: d.count ?? 0,
              hasData: true,
              passAngle: d.passAngle ?? 0,
              passLength: d.passLength ?? 0,
            });
          }
        });
        const colorScale = d3.scaleLinear<string>()
          .domain([10, 30, 50, 65, 100])
          .range(["#FF3864", "#FF7800", "#F2E94E", "#4AF075", "#00D1A8"])
          .interpolate(d3.interpolateLab);

        const tooltip = d3
          .select("body")
          .append("div")
          .attr("class", "custom-tooltip") 
          .style("position", "fixed")
          .style("z-index", "999")
          .style("visibility", "hidden")
          .style("background-color", theme.secondaryButtonBackground)
          .style("color", theme.color)
          .style("border", `1px solid ${theme.secondaryButtonBackground}`)  
          .style("padding", "8px")
          .style("border-radius", "4px")
          .style("box-shadow", "0px 0px 6px rgba(0, 0, 0, 0.1)")
          .style("pointer-events", "none")
          .style("font-size", "12px")
             svg
                    .append("defs")
                    .append("marker")
                    .attr("id", "arrowhead")
                    .attr("viewBox", "0 0 10 10")
                    .attr("refX", 5) // align the center of the `>` tip with the end of the line
                    .attr("refY", 5)
                    .attr("markerWidth", 10)
                    .attr("markerHeight", 10)
                    .attr("orient", "auto")
                    .attr("markerUnits", "userSpaceOnUse") // ensure fixed size
                    .append("path")
                    .attr("d", "M0,0 L5,5 L0,10") // draw the `>` shape
                    .attr("fill", "none")
                    .attr("stroke", "#00A1E4")
                    .attr("stroke-width", 1)

        gridData.forEach((cellData, key) => {
          const [col, row] = key.split(",").map(Number);
          const baseX = xScale(col * cellWidth);
          const baseY = yScale(row * cellHeight);
          const width = xScale(cellWidth) - xScale(0);
          const height = yScale(cellHeight) - yScale(0);
          const x = baseX + padding;
          const y = baseY + padding;
          const adjustedWidth = width - padding * 2;
          const adjustedHeight = height - padding * 2;
          const opacity = cellData.hasData
            ? d3.scaleLinear().domain([0, maxPassCount]).range([0.5, 1])(cellData.passCount)
            : 0.08;

          if (cellData.hasData && cellData.passCount > 0) {
            const minThreshold = 3;
            const minSizeRatio = 0.5;
            const maxSizeRatio = 1.0;
            const fillRatio = cellData.hasData
              ? d3.scaleLinear()
                .domain([minThreshold, maxPassCount])
                .range([minSizeRatio, maxSizeRatio])
                .clamp(true)(cellData.passCount)
              : minSizeRatio;

            const filledWidth = adjustedWidth * fillRatio;
            const filledHeight = adjustedHeight * fillRatio;

            const fillX = x + (adjustedWidth - filledWidth) / 2;
            const fillY = y + (adjustedHeight - filledHeight) / 2;

            const centerX = fillX + filledWidth / 2;
            const centerY = fillY + filledHeight / 2;
            const angleInDegrees = cellData.passAngle; 
            const scaleFactor = 0.6;
            const length = (cellData.passLength); 

            const angleInRadians = (angleInDegrees * Math.PI) / 180;

            const endX = centerX + xScale(length) * Math.cos(angleInRadians);
            const endY = centerY + yScale(length) * Math.sin(angleInRadians);
            let line: any;
            let circle: any;

            const rect = pitchGroup
              .append("rect")
              .attr("x", fillX)
              .attr("y", fillY)
              .attr("width", filledWidth)
              .attr("height", filledHeight)
               .attr("rx", 15 * fillRatio)
               .attr("ry", 15 * fillRatio)
              .style("fill", colorScale(cellData.vaep_score))
              .style("fill-opacity", 1)
              .style("stroke", "none");

            if(cellData.passAngle !== 0 && cellData.passAngle !== null && cellData.passLength !== 0 && cellData.passLength !== null){
                  line = svg
                    .append("line")
                    .attr("x1", centerX)
                    .attr("y1", centerY)
                    .attr("x2", endX)
                    .attr("y2", endY)
                    .attr("stroke", "#00A1E4")
                    .attr("stroke-width", 2)
                    .attr("stroke-opacity", 1)
                    .style("visibility", "hidden")
                    .style("z-index", "999");

                    circle = svg.append('circle')
                                    .attr('cx', centerX)
                                    .attr('cy', centerY)
                                    .attr('r', 4)
                                    .style('fill', "#00A1E4")
                                    .style("visibility", "hidden");

                  
                  line!.attr("marker-end", "url(#arrowhead)");

            }
            
            if (cellData.hasData) {
              rect

                .style("cursor", "pointer")
                .on("mouseover", (event) => {
                  tooltip
                    .style("visibility", "visible")
                    .html(`<strong>Score:</strong> ${cellData.vaep_score.toFixed(2)}<br/>
                          <strong>Count:</strong> ${cellData.passCount}<br/>
                          <strong>Angle:</strong> ${cellData.passAngle.toFixed(2)}<br/>
                          <strong>Length:</strong> ${cellData.passLength.toFixed(2)}`)
                  line?.style("visibility", "visible")!;
                  circle?.style("visibility", "visible")!
                })
                .on("mousemove", (event) => {
                  tooltip
                    .style("top", `${event.clientY + 10}px`)
                    .style("left", `${event.clientX + 10}px`);
                })
                .on("mouseout", () => {
                  tooltip.style("visibility", "hidden");
                  line?.style("visibility", "hidden")!;
                  circle?.style("visibility", "hidden")!
                });
            }
          }
        });

      };
      createPitchGrid(pitchGroup, xScale, yScale, pitchProps.buildupPlayData, getBackgroundColor, maxPassCount);
    }

    //heat map 
    if (pitchProps.currentView === 'attributesOnPitch' && selectedAttr === 'Attack positioning' && pitchProps?.finalThirdData! && pitchProps?.finalThirdData.length > 0) {
      const getBackgroundColor = (vaepScore: number, opacity: any) => {
        if (vaepScore == null) {
          return "none";
        }
        if (vaepScore >= 0 && vaepScore <= 19.99) {
          return "var(--UI-red-500, #FF3864)";
        } else if (vaepScore >= 20 && vaepScore <= 39.99) {
          return "var(--Brand-orange-500, #FF7800)";
        } else if (vaepScore >= 40 && vaepScore <= 59.99) {
          return "var(--UI-yellow-500, #F2E94E)";
        } else if (vaepScore >= 60 && vaepScore <= 79.99) {
          return "var(--Brand-green-2-500, #4AF075)";
        } else if (vaepScore >= 80 && vaepScore <= 100) {
          if (vaepScore >= 98 && opacity >= 0.6) {
            return "#00D1A8";
          } else {
            return "#4AF075";
          }
        } else {
          return "red";
        }
      };

      type ColorFunction = (vaepScore: number, opacity: any) => string;
      const createPitchGrid = (
        pitchGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
        xScale: d3.ScaleLinear<number, number>,
        yScale: d3.ScaleLinear<number, number>,
        attributesData: IFinalThirdPitchData[],
        getBackgroundColor: ColorFunction
      ): void => {
        const gridColumns = 20;
        const gridRows = 20;
        const padding = 3;
        const cellWidth = 105 / gridColumns;
        const cellHeight = 68 / gridRows;
        const gridData = new Map();
        for (let col = 0; col < gridColumns; col++) {
          for (let row = 0; row < gridRows; row++) {
            const key = `${col},${row}`;
            gridData.set(key, {
              vaep_score: null,
              passCount: 0,
              hasData: false,
            });
          }
        }
        attributesData.forEach((d) => {
          const col = Math.floor(d.locationX / cellWidth);
          const row = Math.floor(d.locationY / cellHeight);
          const key = `${col},${row}`;
          if (col >= 0 && col < gridColumns && row >= 0 && row < gridRows) {
            gridData.set(key, {
              vaep_score: d.score,
              hasData: true,
            });
          }
        });
        const colorScale = d3.scaleLinear<string>()
          .domain([10, 30, 50, 65, 100])
          .range(["#FF3864", "#FF7800", "#F2E94E", "#4AF075", "#00D1A8"])
          .interpolate(d3.interpolateLab);

        const tooltip = d3
          .select("body")
          .append("div")
          .attr("class", "custom-tooltip")
          .style("position", "fixed")
          .style("z-index", "999")
          .style("visibility", "hidden")
          .style("background-color", theme.secondaryButtonBackground)
          .style("color", theme.color)
          .style("border", `1px solid ${theme.secondaryButtonBackground}`)
          .style("padding", "8px")
          .style("border-radius", "4px")
          .style("box-shadow", "0px 0px 6px rgba(0, 0, 0, 0.1)")
          .style("pointer-events", "none")
          .style("font-size", "12px")


        gridData.forEach((cellData, key) => {
          const [col, row] = key.split(",").map(Number);
          const baseX = xScale(col * cellWidth);
          const baseY = yScale(row * cellHeight);
          const width = xScale(cellWidth) - xScale(0);
          const height = yScale(cellHeight) - yScale(0);
          const x = baseX + padding;
          const y = baseY + padding;
          const adjustedWidth = width - padding * 2;
          const adjustedHeight = height - padding * 2;

          const rect = pitchGroup
            .append("rect")
            .attr("x", x)
            .attr("y", y)
            .attr("width", adjustedWidth)
            .attr("height", adjustedHeight)
            .attr("rx", 15)
            .attr("ry", 15)
            .style("fill", cellData.hasData ? colorScale(cellData?.vaep_score!) : "#A2A4A1")
            .style("fill-opacity", cellData.vaep_score > 0 ? 1 : 0.08)
            .style("stroke", "rgba(255,255,255,0.1)")
            .style("stroke-width", 1);

          if (cellData.hasData) {
            rect

              .style("cursor", "pointer")
              .on("mouseover", (event) => {
                tooltip
                  .style("visibility", "visible")
                  .html(`<strong>Score:</strong> ${cellData.vaep_score.toFixed(2)}<br/>`);
              })
              .on("mousemove", (event) => {
                tooltip
                  .style("top", `${event.clientY + 10}px`)
                  .style("left", `${event.clientX + 10}px`);
              })
              .on("mouseout", () => {
                tooltip.style("visibility", "hidden");
              });
          }
        });

      };
      createPitchGrid(pitchGroup, xScale, yScale, pitchProps.finalThirdData, getBackgroundColor);

    }

    if (pitchProps.currentView === "attributesOnPitch" && (selectedAttr === 'Tackling' || selectedAttr === 'Defensive headers' || selectedAttr === 'Ball recovery') && pitchProps?.defenceData!) {
          pitchProps.defenceData.outerHull.forEach((d) => {
            pitchGroup
              .append("circle")
              .attr("cx", xScale(d.locationX))
              .attr("cy", yScale(d.locationY))
              .attr("r", 8 * scale)
              .style("fill", d.isSuccessFul ? "#00D1A8" : "#FF3864")
              .style('opacity', 0.7);
          });
          if (pitchProps.defenceData.outerHull?.length >= 3) {
            const hullPoints = d3.polygonHull(
              pitchProps.defenceData.outerHull.map((d) => [
                xScale(d.locationX),
                yScale(d.locationY),
              ])
            );
            const lineGenerator = d3
              .line()
              .x((d) => d[0])
              .y((d) => d[1])
              .curve(d3.curveLinearClosed);
            if (pitchProps.defenceData.innerHull?.length >= 3) {
              const innerHullPoints = d3.polygonHull(
                pitchProps.defenceData.innerHull.map((d) => [
                  xScale(d.locationX),
                  yScale(d.locationY),
                ])
              );
              if (innerHullPoints) {
                pitchGroup
                  .append("path")
                  .attr("d", lineGenerator(innerHullPoints))
                  .style("fill", "#4AF07580")
                  .style("fill-opacity", '50%')
                  .style("stroke", "#4AF07580")
                  .style("stroke-width", 1);
              }

            }
      }
      
    }


    {
      // Draw Left Penalty Arc
      drawArc(
        pitchGroup,
        105 * scale,
        pitchHeight / 2,
        50 * scale,
        Math.PI * 0.85,
        Math.PI - Math.PI * 0.85,
        theme.NumberColor,
        "none",
        0.5
      );
      // Draw Center Circle
      drawCircle(pitchGroup, pitchWidth / 2, pitchHeight / 2, 60 * scale, "none", 0.5, theme.NumberColor, 1);
      // Draw Centre Spot
      drawCircle(pitchGroup, xScale(105 / 2), yScale(68 / 2), 5 * scale, theme.NumberColor, 0.5);
      // Draw Center Line
      drawLine(pitchGroup, pitchWidth / 2, 0, pitchWidth / 2, pitchHeight, theme.NumberColor, 1, undefined, 0.5);
      // Draw Left 6-yard Box
      drawRect(
        pitchGroup,
        xScale(0),
        yScale(25.16),
        xScale(6.3) - xScale(0),
        yScale(42.84) - yScale(25.16),
        theme.NumberColor,
        "none",
        1,
        0.5
      );
      // Draw Left Penalty Area
      drawRect(
        pitchGroup,
        xScale(0),
        yScale(12.92),
        xScale(16.8) - xScale(0),
        yScale(55.08) - yScale(12.92),
        theme.NumberColor,
        "none",
        1,
        0.5
      );
      // Draw Center Circle
      // drawCircle(pitchGroup, pitchWidth / 2, pitchHeight / 2, 60 * scale, "none", 0.5, theme.NumberColor, 1);
      // Draw Right Penalty Area
      drawRect(
        pitchGroup,
        xScale(88.2),
        yScale(12.92),
        xScale(105) - xScale(88.2),
        yScale(55.08) - yScale(12.92),
        theme.NumberColor,
        "none",
        1,
        0.5
      );
      //Draw Right 6-yard Box
      drawRect(
        pitchGroup,
        xScale(98.7),
        yScale(25.16),
        xScale(105) - xScale(98.7),
        yScale(42.84) - yScale(25.16),
        theme.NumberColor,
        "none",
        1,
        0.5
      );
      // Draw Right Penalty Arc
      drawArc(
        pitchGroup,
        pitchWidth - 105 * scale,
        pitchHeight / 2,
        50 * scale,
        Math.PI * 1.15,
        Math.PI + Math.PI * 0.85,
        theme.NumberColor,
        "none",
        0.5
      );
      // Draw Left Penalty Spot
      drawCircle(pitchGroup, xScale(10.5), yScale(68 / 2), 5 * scale, theme.NumberColor, 0.5);
      // Draw Right Penalty Spot
      drawCircle(pitchGroup, xScale(94.5), yScale(68 / 2), 5 * scale, theme.NumberColor, 0.5);
      // Draw Center Half
      drawCircle(pitchGroup, xScale(94.5), yScale(68 / 2), 5 * scale, theme.NumberColor, 0.5);
      // Define corner data
      {
        if (pitchProps.selectedTeam! < 1 && pitchProps.currentView !== "passingNetwork") {
          drawMiddleRect(svg, xScale, yScale, theme, TeamSelect, ["Select a team", "for the pitch"]);
        }
        if (pitchProps.currentView === "passingNetwork" && pitchProps.selectedMatch === 0 && pitchProps.passNetworkPlayers?.length! < 1) {
          drawMiddleRect(svg, xScale, yScale, theme, TeamSelect, ["Select a", "match first"]);
        }
      }


      {
        pitchProps.currentView === "optimalPathways" &&
          pitchProps.positionResults?.map((player) => {
            drawPlayerImages(
              svg,
              xScale,
              yScale,
              pitchProps.setSwapHoverPlayer!,
              player,
              pitchProps?.positionResults!,
              theme,
              pitchProps.isIndividualPassing!,
              undefined,
              pitchProps.handleReset,
              pitchProps.setIsIndividualPassing!,
              pitchProps.handleIndividualPassing!
            );
          }) &&
          drawPlayerNames(svg, xScale, yScale, pitchProps.positionResults!, theme, pitchProps.isPerformancSwitchToggled!);
      }
      {
        pitchProps.currentView === "areasOfControl" &&
          pitchProps.hullData?.length! < 1 &&
          drawPlayerNames(svg, xScale, yScale, pitchProps.positionResults!, theme, pitchProps.isPerformancSwitchToggled!);
      }
      {
        pitchProps.currentView === "systemFit" &&
          pitchProps?.isChemistryDataNotEnough &&
          !pitchProps?.isSystemfitLoading &&
          drawMiddleRect(svg, xScale, yScale, theme, TeamSelect, ["Not enough data", "for the season"]);
      }
      {
        pitchProps.currentView === "systemFit" &&
          pitchProps.selectedTeam! > 0 &&
          !pitchProps.isChemistryDataNotEnough &&
          drawChemistrys(
            pitchGroup,
            xScale,
            yScale,
            pitchProps.positionResults!,
            pitchProps.alreadyPlottedPlayer!,
            pitchProps.setIsIndividualChemistry!,
            pitchProps.handleIndividualChemistry!,
            pitchProps.isIndividualChemistry!,
            pitchProps.setSwapHoverPlayer!,
            pitchProps.swappedPlayerClub!,
            pitchProps.otherClubPlayer!,
            pitchProps.setSidebarPlayerShow!,
            pitchProps.setIsReplacePlayer!,
            pitchProps.setSwapPositionCode!,
            pitchProps.setPlayerCardID!,
            pitchProps.teamChemistry!,
            pitchProps.getChemistrybetweenPlayers!,
            pitchProps.isPerformancSwitchToggled!,
            pitchProps.handleReset!,
            pitchProps.formationName!,
            theme,
            pitchProps?.playerCardID!,
            pitchProps.oldPlayerId!,
            pitchProps.newPlayerId!,
            pitchProps?.handleSwapPlayer!,
            pitchProps?.selectedByPlayerPair!,
            pitchProps?.synergyData,
            pitchProps?.isForSimulation!
          );
      }
      {
        pitchProps.currentView === "moduleChemistry" &&
          drawModuleChemistrys(
            pitchGroup,
            xScale,
            yScale,
            pitchProps.positionResults!,
            pitchProps.teamChemistry!,
            false,
            pitchProps.modulefitPlayer!,
            theme
          );
      }
      {
        pitchProps.currentView === "fullModuleChemistry" &&
          drawModuleChemistrys(
            pitchGroup,
            xScale,
            yScale,
            pitchProps.positionResults!,
            pitchProps.teamChemistry!,
            true,
            pitchProps.modulefitPlayer!,
            theme
          );
      }
      {
        pitchProps.currentView === "passingNetwork" &&
          pitchProps.selectedTeam! > 0 &&
          drawPlayerPassingNetwork(
            svg,
            pitchGroup,
            xScale,
            yScale,
            pitchProps.setSwapHoverPlayer!,
            pitchProps.setSidebarPlayerShow!,
            pitchProps.setSwapPositionCode!,
            pitchProps.setPlayerCardID!,
            pitchProps.passNetworkPlayers!,
            pitchProps.passNetworkPlayerPasses!,
            pitchProps.isPerformancSwitchToggled!,
            pitchProps.isIndividualPassing!,
            pitchProps.setIsIndividualPassing!,
            pitchProps.handleIndividualPassing!,
            pitchProps.handleReset!,
            theme
          );
      }
      {
        pitchProps.currentView === "areasOfControl" &&
          pitchProps.selectedTeam! > 0 &&
          drawHull(
            svg,
            pitchGroup,
            xScale,
            yScale,
            pitchProps.hullData!,
            pitchProps.isPerformancSwitchToggled!,
            pitchProps.setIndividualHullPlayer!,
            pitchProps.individualHullPlayer!,
            theme
          );
      }
      
      {
        (pitchProps.currentView === "GameModel" || pitchProps.currentView === "passingNetwork") && pitchProps.gameModelData && (
          (() => {
            // Extract IGameModel[] from the union type
            const gameModelDataArray = Array.isArray(pitchProps.gameModelData)
              ? pitchProps.gameModelData
              : (pitchProps.gameModelData as IGameModelSimulation).outGameModel;
            drawGameModelContours(pitchGroup, xScale, yScale, pitchWidth, pitchHeight, gameModelDataArray, tooltipRef, pitchProps.selectedMatch);

            // Draw gameModel players if available
            if (pitchProps.gameModelPlayers && pitchProps.currentView === "GameModel") {
              drawGameModelPlayers(
                svg,
                xScale,
                yScale,
                pitchProps.gameModelPlayers,
                theme,
                pitchProps.setSidebarPlayerShow!,
                pitchProps.setPlayerCardID!,
                pitchProps.isVerticalGameModel
              );
            }
          })()
        );
      }
      {
        pitchProps.riskMapData &&
          pitchProps.currentPlayView === "threatMap" &&
          generateRiskMap(riskMapGroup, xScale, yScale, pitchProps.riskMapData, pitchWidth, pitchHeight);
      }
      {
        pitchProps.passingAttackData &&
          pitchProps.currentView === "passingAttack" &&
          renderPassingAttack(riskMapGroup, xScale, yScale, pitchProps.passingAttackData, tooltipRef);
      }
      {
        pitchProps.attributesPitchData &&
          renderRiskMapForAttributes(riskMapGroup, xScale, yScale, pitchProps.attributesPitchData, tooltipRef);
      }
    }

    const corners = [
      { x: xScale(105), y: yScale(68), startAngle: 6.3, endAngle: 1.5 * Math.PI },
      { x: xScale(0), y: yScale(68), startAngle: 1.6, endAngle: 0 },
      { x: xScale(0), y: yScale(0), startAngle: 1.6, endAngle: Math.PI },
      { x: xScale(105), y: yScale(0), startAngle: 4.7, endAngle: Math.PI },
    ];

    // Draw arcs for all corners
    // {
    //   pitchProps.isCorner &&
    //     corners.forEach((corner) => {
    //       drawCornerArc(
    //         pitchGroup,
    //         25 * scale,
    //         25 * scale,
    //         corner.startAngle,
    //         corner.endAngle,
    //         corner.x,
    //         corner.y,
    //         theme.PitchColor,
    //         theme.NumberColor,
    //         0.5
    //       );
    //     });
    // }

    let shiftPressed = false;
    // Check if Shift key is pressed
    const handleKeyPress = (event: { shiftKey: boolean }) => {
      shiftPressed = event.shiftKey;
    };
    window.addEventListener("keydown", handleKeyPress);
    window.addEventListener("keyup", () => {
      shiftPressed = false;
    });
    let currentScale = 1;
    const zoomed = ({ transform }: { transform: d3.ZoomTransform }): void => {
      if (!shiftPressed) {
        return;
      }
      currentScale = transform.k;
      const xTranslate = Math.min(0, Math.max(+svg.attr("width") * (1 - currentScale), transform.x));
      const yTranslate = Math.min(0, Math.max(+svg.attr("height") * (1 - currentScale), transform.y));
      pitchGroup.attr("transform", `translate(${xTranslate},${yTranslate}) scale(${currentScale})`).style("overflow", "hidden");
    };

    svg.call(
      d3
        .zoom<SVGSVGElement, unknown>()
        .scaleExtent([1, 10]) // Adjusted lower limit to 1 to prevent zooming out beyond the original size
        .on("zoom", zoomed)
    );
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
      window.removeEventListener("keyup", handleKeyPress);
    };


  }, [
    theme,
    selectedAreas,
    pitchProps.selectedZones,
    pitchProps.isZones,
    pitchProps.isPerformancSwitchToggled,
    pitchProps.positionResults,
    pitchProps.passNetworkPlayers,
    pitchProps.currentView,
    pitchProps.riskMapData,
    pitchProps.hullData,
    pitchProps.currentPlayView,
    pitchProps.individualHullPlayer,
    pitchProps.passingAttackData,
    pitchProps.passNetworkPlayerPasses,
    pitchProps.teamChemistry,
    pitchProps.isChemistryDataNotEnough,
    pitchProps.playerCardID,
    pitchProps.attributesPitchData,
    pitchProps.finalThirdData,
    pitchProps.defenceData,
    selectedAttr,
    pitchProps.buildupPlayData,
    pitchProps.selectedByPlayerPair,
    pitchProps.synergyData,
    pitchProps.gameModelData,
    pitchProps.isForSimulation
  ]);

  useEffect(() => {
    createFootballPitch();
    const handleResize = () => {
      if (pitchRef.current) {
        const pitchWidth = pitchRef.current.clientWidth;
        const pitchHeight = pitchWidth * 0.625; // Maintain 5:8 aspect ratio
        setPitchDimensions({ width: pitchWidth, height: pitchHeight });
      }
      // const container = document.getElementById("football-pitch-container")!;
      // setPitchDimensions({ width: container.clientWidth, height: container.clientWidth * 0.625 });
      createFootballPitch();
    };
    window.addEventListener("resize", handleResize);
    const handleOrientationChange = () => {
      createFootballPitch();
    };
    window.addEventListener("orientationchange", handleOrientationChange);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleOrientationChange);
    };
  }, [createFootballPitch]);

  const handleClose = () => {
    setShowPlayerCard(!showPlayerCard);
  };

  const themeMode = useRef(theme.mode);

  useEffect(() => {
    themeMode.current = theme.mode;
  }, [theme]);

  return (
    <div id="football-pitch-container" ref={pitchRef}>
      <div style={{ position: "absolute", display: pitchProps.currentPlayView === "flowOfPlay" ? "block" : "none" }}>
        <Playfield
          theme={themeMode.current}
          playOnStart={true}
          axis={pitchProps.currentPitchAxis}
          data={{
            mode: SingleGameVisualisationMode.TEAM,
            data: pitchProps.flowData!,
          }}
          width={pitchWidth}
          height={pitchHeight}
          settings={{ engine: "rework", debug: true }}
          players={pitchProps.passNetworkPlayers}
        />
      </div>
      <svg
        ref={svgRef}
        style={{
          border:`1px solid #b2b2b2`,
          borderRadius:'8px',
          transform: pitchProps.isVerticalGameModel ? 'rotate(-90deg) scale(1)' : 'none',
          transformOrigin: 'center'
        }}
        id={"pitch-image"}
      >
      </svg>

      <div
        id="tooltip"
        style={{
          position: "absolute",
          opacity: 0,
          background: "white",
          padding: "5px",
          // border: "1px solid black",
          boxShadow: "0px 0px 6px rgba(0, 0, 0, 0.1)",
          color: "black",
        }}
        ref={tooltipRef}
      ></div>
      {showPlayerCard && (
        <div
          style={{
            position: "absolute",
            top: cardPosition.y,
            left: cardPosition.x,
          }}
        >
          <PlayerCard player={selectedPlayer} cardClose={handleClose} />
        </div>
      )}
    </div>
  );
};

export default Pitch;
